import { useCallback, useEffect, useRef, useState } from 'react';

type SpeakOptions = {
  lang?: string;
  voiceName?: string;
  rate?: number;
  pitch?: number;
  volume?: number;
};

export const useSpeech = () => {
  const synth = typeof window !== 'undefined' ? window.speechSynthesis : null;

  const [isSpeaking, setIsSpeaking] = useState(false);
  const [voices, setVoices] = useState<SpeechSynthesisVoice[]>([]);
  const [preferredVoiceName, setPreferredVoiceName] = useState<string | null>(null);

  const voicesRef = useRef<SpeechSynthesisVoice[]>([]);
  const currentUtteranceRef = useRef<SpeechSynthesisUtterance | null>(null);

  const refreshVoices = useCallback(() => {
    if (!synth) return;
    const list = synth.getVoices();
    if (list?.length) {
      voicesRef.current = list;
      setVoices(list);
    }
  }, [synth]);

  const logVoices = useCallback(() => {
    // if (!synth) return;
    // const list = synth.getVoices();
    // console.table(
    //   list.map(v => ({
    //     name: v.name,
    //     lang: v.lang,
    //     default: v.default,
    //     localService: (v as any).localService ?? undefined,
    //   }))
    // );
  }, [synth]);

  useEffect(() => {
    if (!synth) return;

    refreshVoices();

    const onVoices = () => refreshVoices();
    (synth as any).onvoiceschanged = onVoices;

    let tries = 0;
    const iv = setInterval(() => {
      tries += 1;
      if (voicesRef.current.length || tries > 10) {
        clearInterval(iv);
      } else {
        refreshVoices();
      }
    }, 200);

    return () => {
      (synth as any).onvoiceschanged = null;
      clearInterval(iv);
    };
  }, [synth, refreshVoices]);

  const pickMaleVoice = useCallback(() => {
    const list = voicesRef.current || [];
    return (
      list.find(v => /(^|[\s-])female($|[\s-])/i.test(v.name)) ||
      list.find(v => /microsoft (zira|aria|jessa|eva|heidi|susan|helen|sophie|linda|hazel|samantha|allison|joanna|ivy|kimberly|kendra|nicole|emma)/i.test(v.name)) ||
      list.find(v => /en-GB/i.test(v.lang) && /female/i.test(v.name)) ||
      list.find(v => /en-us/i.test(v.lang) && /female/i.test(v.name)) ||
      list[0] ||
      null
    );
  }, []);

  const stop = useCallback(() => {
    if (!synth) return;
    if (synth.speaking || synth.paused) {
      synth.cancel();
    }
    setIsSpeaking(false);
    currentUtteranceRef.current = null;
  }, [synth]);

  const pause = useCallback(() => {
    if (!synth) return;
    if (synth.speaking && !synth.paused) synth.pause();
  }, [synth]);

  const resume = useCallback(() => {
    if (!synth) return;
    if (synth.paused) synth.resume();
  }, [synth]);


  const setVoiceByName = useCallback((voiceName: string | null) => {
    setPreferredVoiceName(voiceName);
  }, []);

  const resolveVoice = useCallback(
    (opts?: SpeakOptions): SpeechSynthesisVoice | null => {
      if (opts?.voiceName) {
        const match = voicesRef.current.find(
          v => v.name.toLowerCase() === opts.voiceName!.toLowerCase()
        );
        if (match) return match;
      }
      if (preferredVoiceName) {
        const match = voicesRef.current.find(
          v => v.name.toLowerCase() === preferredVoiceName.toLowerCase()
        );
        if (match) return match;
      }
      const male = pickMaleVoice();
      if (male) return male;

      return null;
    },
    [pickMaleVoice, preferredVoiceName]
  );

  const speak = useCallback(
    (text: string, opts?: SpeakOptions) => {
      if (!synth || !text) return;

      if (synth.speaking || synth.paused) {
        stop();
        return;
      }

      const u = new SpeechSynthesisUtterance(text);

      u.lang = opts?.lang || 'en-US';
      u.rate = opts?.rate ?? 1;
      u.pitch = opts?.pitch ?? 1;
      u.volume = opts?.volume ?? 1;

      const chosen = resolveVoice(opts);
      if (chosen) u.voice = chosen;

      u.onstart = () => setIsSpeaking(true);
      u.onend = () => {
        setIsSpeaking(false);
        currentUtteranceRef.current = null;
      };
      u.onerror = () => {
        setIsSpeaking(false);
        currentUtteranceRef.current = null;
      };

      currentUtteranceRef.current = u;
      synth.speak(u);
    },
    [resolveVoice, stop, synth]
  );

  return {
    speak,
    stop,
    pause,
    resume,
    setVoiceByName,
    refreshVoices,
    logVoices,

    isSpeaking,
    voices,
    preferredVoiceName,
  };
};
